import { useMemo } from "react";
import { useGame } from "@/context/GameContext";
import { EquipmentDoll } from "@/components/game/EquipmentDoll";
import { ITEM_ICON, itemName } from "@/lib/gameData";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Shield, Swords } from "lucide-react";

/**
 * Full-page Equipment panel. Left: paper-doll silhouette. Right: bag of
 * equippable items you can quickly slot in (currently weapons only).
 */

export function EquipmentPanel() {
  const { snap, content, equip } = useGame();
  const items = content?.items || {};

  const equippables = useMemo(() => {
    if (!snap?.inventory) return [];
    return snap.inventory.filter((s) => {
      const it = items[s.item];
      return it && (it.cat === "weapon" || it.cat === "armour");
    });
  }, [snap?.inventory, items]);

  async function equipItem(id) {
    const it = items[id];
    const slot = it?.cat === "weapon" ? "weapon" : it?.slot;
    try {
      await equip(id, slot);
      toast.success(`Equipped ${itemName(id, items)}.`);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="p-4 lg:p-6 space-y-3" data-testid="equipment-panel">
      {/* Tabs (mirrors Profile / Skills panels) */}
      <div className="flex items-center gap-2 text-xs font-body px-1">
        <button className="text-amber-400 border-b-2 border-amber-500 pb-1 px-1" data-testid="tab-equipment">Equipment</button>
        <button className="text-stone-500 hover:text-amber-500 pb-1 px-1" title="Coming soon">Loadouts</button>
        <div className="flex-1" />
        <span className="text-[10px] font-numeric text-stone-500 uppercase tracking-widest">
          Every equipped piece contributes to combat stats
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Portrait band */}
        <div className="lg:col-span-12">
          <div
            className="panel overflow-hidden relative"
            style={{
              backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.55), rgba(12,10,9,0.92)), url(/generated/hero_landing.png)`,
              backgroundSize: "cover", backgroundPosition: "center",
            }}
          >
            <div className="p-6 flex items-center gap-6">
              <div className="w-20 h-20 rounded-lg border-2 border-amber-700 bg-stone-950 flex items-center justify-center">
                <Shield size={30} className="text-amber-400" />
              </div>
              <div className="flex-1">
                <h1 className="font-display text-4xl heading-gilded">Equipment</h1>
                <p className="text-stone-400 font-body italic text-sm">
                  Paper-doll of what you carry into a fight. Hover a slot to unequip.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Doll */}
        <div className="lg:col-span-6 flex justify-center items-start">
          <div className="w-full max-w-sm">
            <EquipmentDoll />
          </div>
        </div>

        {/* Equippable inventory bag */}
        <div className="lg:col-span-6">
          <div className="panel p-3">
            <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 mb-2">
              Equippable in your bag ({equippables.length})
            </h3>
            {equippables.length === 0 ? (
              <div className="text-stone-500 italic text-sm py-8 text-center font-body">
                Nothing wearable in your backpack. Try crafting a bronze sword or a bow.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="equippable-list">
                {equippables.map((slot) => {
                  const it = items[slot.item];
                  const targetSlot = it.cat === "weapon" ? "weapon" : it.slot;
                  const isEquipped = snap?.equipment?.[targetSlot] === slot.item;
                  const stats = [];
                  if (it.atk) stats.push(`+${it.atk} ATK`);
                  if (it.str) stats.push(`+${it.str} STR`);
                  if (it.def) stats.push(`+${it.def} DEF`);
                  if (it.speed) stats.push(`SPD ${it.speed}t`);
                  return (
                    <div
                      key={slot.item}
                      className="panel-inset p-2 flex items-center gap-3"
                      data-testid={`equippable-${slot.item}`}
                    >
                      <div className="w-12 h-12 inv-slot text-2xl">
                        {ITEM_ICON[slot.item] || "◇"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-display text-sm text-stone-100 truncate">{it.name}</span>
                          <span className="text-[9px] font-numeric uppercase tracking-widest bg-stone-900 border border-stone-700 rounded px-1 text-stone-500">
                            {targetSlot}
                          </span>
                          {it.two_hand && (
                            <span
                              className="text-[9px] font-numeric uppercase tracking-widest bg-amber-900/40 border border-amber-700 rounded px-1 text-amber-300"
                              title="Two-handed — equipping this will move your shield to your bag."
                              data-testid={`badge-2h-${slot.item}`}
                            >
                              2H
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] font-numeric text-stone-500">
                          {stats.join(" · ") || it.cat}
                        </div>
                        <div className="text-[10px] font-numeric text-stone-500">Qty · {slot.qty}</div>
                      </div>
                      <button
                        onClick={() => equipItem(slot.item)}
                        disabled={isEquipped}
                        className={
                          "text-[10px] px-2 py-1 rounded border font-body shrink-0 transition-colors " +
                          (isEquipped
                            ? "bg-stone-900 border-stone-700 text-stone-500 cursor-not-allowed"
                            : "bg-amber-800 hover:bg-amber-700 border-amber-600 text-amber-50")
                        }
                        data-testid={`equip-${slot.item}`}
                      >
                        {isEquipped ? "Equipped" : "Equip"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="panel p-3 mt-3">
            <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 mb-1.5 flex items-center gap-1.5">
              <Swords size={11} /> Slot roadmap
            </h3>
            <p className="text-[11px] italic text-stone-500 font-body leading-snug">
              Every equipped piece adds to your Attack, Strength or Defence bonuses. Head, body, legs,
              cape, amulet, ammo, shield, gloves, boots and ring — each meaningfully changes how you
              fight and how much you shrug off.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
